import { formErrorsFromFields, type FormErrors } from '@/lib/form-errors';
import { leadStatusOrder } from '@/theme/status';
import type { EstadoLead } from '@/types/domain';
import { formatNumber, MOTIVO_MAX_CHARS, PURGA_DIAS_MAX, PURGA_LIMITE_MAX, RECALCULO_LIMIT_MAX } from './crm-format';
import { parseRecalculoLimite } from './crm-payload';

/**
 * Validación de los formularios del CRM, con la forma que consumen `Field` y `FormErrorAlert`.
 *
 * Son formularios cortos y el backend vuelve a validar todo (`crm.schemas.ts`), así que acá solo se
 * comprueba lo que el usuario puede arreglar antes de gastar la petición: que haya un estado
 * elegido, que el motivo entre en los 300 caracteres del schema y que el tope del recálculo esté
 * dentro de 1-2000.
 */

export const CRM_LIMITS = {
  /** `motivo` del `PATCH .../estado` (texto libre, `max(300)` en el schema). */
  motivo: MOTIVO_MAX_CHARS,
  /** Tope de conversaciones del recálculo masivo (`1-2000`). */
  recalculo: RECALCULO_LIMIT_MAX,
} as const;

/** Los 4 estados del kanban, en el orden del tablero (el mismo de `leadStatusOrder`). */
export const LEAD_ESTADOS = leadStatusOrder;

/** `estadoLead` — obligatorio y uno de los 4 del enum. */
export function validateLeadEstado(value: EstadoLead | ''): string | undefined {
  if (value === '') return 'Elige el estado del lead';
  if (!LEAD_ESTADOS.includes(value)) return 'El estado del lead no es válido';

  return undefined;
}

/** `motivo` — opcional, hasta 300 caracteres (el backend lo recorta con `trim`). */
export function validateMotivo(value: string): string | undefined {
  if (value.trim().length > CRM_LIMITS.motivo) {
    return `El motivo no puede superar los ${CRM_LIMITS.motivo} caracteres`;
  }

  return undefined;
}

/** Validación completa del formulario de reclasificación. */
export function validateStatusForm(estadoLead: EstadoLead | '', motivo: string): FormErrors {
  return formErrorsFromFields({
    estadoLead: validateLeadEstado(estadoLead),
    motivo: validateMotivo(motivo),
  });
}

/**
 * Validación del tope del recálculo masivo.
 *
 * `parseRecalculoLimite` ya deja cualquier texto dentro de rango; el error solo aparece cuando lo
 * escrito no es un número, porque entonces el admin creería estar recalculando 2000 leads cuando en
 * realidad se está usando el default (500).
 */
export function validateRecalculoForm(limite: string): FormErrors {
  const texto = limite.trim();
  const parsed = Number(texto.replace(',', '.'));

  return formErrorsFromFields({
    limite:
      texto.length === 0 || !Number.isFinite(parsed)
        ? 'Escribe cuántas conversaciones revisar (por ejemplo 500)'
        : undefined,
  });
}

/** El tope que de verdad se va a mandar (útil para el texto de ayuda del diálogo). */
export function describeRecalculoLimite(limite: string): string {
  return String(parseRecalculoLimite(limite));
}

/**
 * Validación del formulario de la PURGA.
 *
 * Igual que en el recálculo, lo que arruina la corrida es un texto que no sea número (el resto cae
 * dentro de rango con `parsePurgaDias`/`parsePurgaLimite`). Acá SÍ se avisa del rango, y no solo de
 * «no es número», porque estos dos campos deciden QUÉ SE BORRA: un 0 en los días borraría
 * conversaciones de hoy mismo, y un tope creído más alto del permitido daría la falsa sensación de
 * que se van a borrar todas las candidatas de una vez.
 */
export function validatePurgaForm(diasSinActividad: string, limite: string): FormErrors {
  return formErrorsFromFields({
    diasSinActividad: validateRango(diasSinActividad, 1, PURGA_DIAS_MAX, 'días sin actividad'),
    limite: validateRango(limite, 1, PURGA_LIMITE_MAX, 'conversaciones'),
  });
}

/** Texto → el mensaje de error si no es un entero dentro de `[minimo, maximo]`. */
function validateRango(
  valor: string,
  minimo: number,
  maximo: number,
  etiqueta: string,
): string | undefined {
  const texto = valor.trim();
  const parsed = Number(texto.replace(',', '.'));
  const rango = `entre ${formatNumber(minimo)} y ${formatNumber(maximo)}`;

  if (texto.length === 0 || !Number.isFinite(parsed)) {
    return `Escribe cuántos ${etiqueta} (${rango})`;
  }

  const entero = Math.trunc(parsed);
  if (entero < minimo || entero > maximo) {
    return `Escribe un número ${rango} ${etiqueta}`;
  }

  return undefined;
}
